import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { HeroQuoteForm } from './HeroQuoteForm';
import { Frame } from './Frame';
import { GoogleReviews } from './GoogleReviews';
import { HeroBlueprint } from './HeroBlueprint';
import { heroTags } from '../data/heroTags';
import { heroStats } from '../data/stats';
const ease = [0.23, 1, 0.32, 1] as const;
const PORTRAIT = 'https://cdn.magicpatterns.com/uploads/aqvUUHV2E7Uhyq1rY99QtV/9ef6dca0-2ce2-48e9-9a4c-d9ce7642602a.jpg';
const softShadow = 'shadow-[0_0_0_1px_rgba(70,28,64,0.07),0_1px_2px_rgba(70,28,64,0.05),0_4px_12px_-2px_rgba(70,28,64,0.06)]';
export function Hero() {
  const reduce = useReducedMotion();
  const fadeUp = (delay: number) => ({
    initial: reduce ? false : {
      opacity: 0,
      y: 10
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.28,
      ease,
      delay
    }
  });
  const left = heroTags.filter(t => t.side === 'left');
  const right = heroTags.filter(t => t.side === 'right');
  return <section id="top" aria-labelledby="hero-title" data-id="element-268" data-mp="3j89618jhw3nf">
      <Frame className="overflow-hidden" data-id="element-269" data-mp="3j6fvvj9ob1ao">
        <HeroBlueprint data-id="element-270" data-mp="3o7vk1k75dnxa" />
        <div className="relative flex flex-col items-center px-5 pb-14 pt-12 text-center md:px-6 md:pb-12 md:pt-[72px] lg:pb-14" data-id="element-271" data-mp="3o6arg04e8f22">
          <motion.div {...fadeUp(0)} className={`inline-flex items-center gap-2 rounded-[5px] bg-white px-3 py-[7px] font-mono text-[13px] leading-none text-muted ${softShadow}`} data-id="element-272" data-mp="39cfofaqx5jys">
            <span className="h-[7px] w-[7px] rounded-[1px] bg-ok" aria-hidden="true" data-id="element-273" data-mp="358re4kawka9u" />
            kapazität <span className="text-ink/30" data-id="element-274" data-mp="2qjv8sv08dbcx">/</span> <span className="text-ink" data-id="element-275" data-mp="2qkwk90l5rzv6">verfügbar</span>
          </motion.div>

          <motion.dl {...fadeUp(0.03)} className="mt-5 grid grid-cols-2 gap-y-3 font-mono text-[14px] leading-none sm:flex sm:items-center sm:divide-x sm:divide-line md:text-[15px]" data-id="element-276" data-mp="2vieuqvetqn5a">
            {heroStats.map(stat => <div key={stat.label} className="flex items-baseline justify-center gap-2 px-4 sm:first:pl-0 sm:last:pr-0 md:px-5" data-id="element-277" data-mp="2lmv0edfwrwin">
                <dt className="order-2 text-muted" data-id="element-278" data-mp="nbl2wm6daiaz">{stat.label}</dt>
                <dd className="order-1 font-medium text-ink" data-id="element-279" data-mp="n98cdkmjmzmb">{stat.value}</dd>
              </div>)}
          </motion.dl>

          <motion.h1 {...fadeUp(0.05)} id="hero-title" className="hero-title mt-8 max-w-[900px] pb-[0.08em] font-display text-[42px] font-normal leading-[1.04] tracking-[-0.045em] text-ink sm:text-[58px] lg:text-[70px]" data-id="element-280" data-mp="ddf44dfuxsac">
            Von der Skizze zur
            <br className="hidden sm:block" data-id="element-281" data-mp="112g7vndqwf9a" /> fertigungsreifen Konstruktion.
          </motion.h1>

          <motion.p {...fadeUp(0.1)} className="mt-6 max-w-[600px] text-[17px] leading-[1.6] text-ink/80 md:text-[19px]" data-id="element-282" data-mp="x1ecjhmy8ncl">
            Bearbeitbare 3D-Modelle, Baugruppen und technische Zeichnungen – aus Skizzen, vorhandenen Daten oder
            Konzepten. Ein Ansprechpartner bis zu fertigungsgerechten Daten.
          </motion.p>

          <motion.div {...fadeUp(0.15)} className="mt-9 flex w-full justify-center" data-id="element-283" data-mp="1xam5wruslszg">
            <HeroQuoteForm data-id="element-284" data-mp="21by07aom32dz" />
          </motion.div>

          <motion.div {...fadeUp(0.2)} className="mt-10" data-id="element-285" data-mp="218jw9y0aul4f">
            <GoogleReviews data-id="element-286" data-mp="2697m4gcskvx2" />
          </motion.div>

          <motion.div initial={reduce ? false : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease,
          delay: 0.2
        }} className="mt-12 grid w-full max-w-[1000px] items-center gap-8 md:mt-20 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:gap-0" data-id="element-287" data-mp="2b0pnv6mn30j3">
            <TagColumn tags={left} side="left" data-id="element-288" data-mp="2p1jvhn1v4xgp" />

            <figure className="relative mx-auto w-[264px] sm:w-[320px]" data-id="element-289" data-mp="2tzlgidiun6ut">
              <div className={`relative rounded-[8px] bg-white p-1.5 ${softShadow}`} data-id="element-290" data-mp="2tysh5xm7zjmp">
                <div className="aspect-[4/5] overflow-hidden rounded-[5px] bg-canvas" data-id="element-291" data-mp="uo0svysz6ooj">
                  <img src={PORTRAIT} alt="Portrait von Konrad Walz im hellen Anzug vor einem modernen Bürogebäude" width={640} height={640} decoding="async" className="h-full w-full object-cover object-[50%_30%] [filter:brightness(1.1)_contrast(1.05)]" data-id="element-292" data-mp="zmc2c8fcyx68" />
                </div>
              </div>
              <figcaption className="mt-4 font-mono text-[11px] uppercase tracking-[0.12em] text-muted" data-id="element-293" data-mp="98e2hpn4vaf7">
                <span className="text-ink" data-id="element-294" data-mp="3hnxs8eoliapi">Konrad Walz</span> · CAD-Konstrukteur
              </figcaption>
            </figure>

            <TagColumn tags={right} side="right" data-id="element-295" data-mp="3rglq7crbakpt" />
          </motion.div>
        </div>
      </Frame>
    </section>;
}
type TagColumnProps = {
  tags: typeof heroTags;
  side: 'left' | 'right';
};
function TagColumn({
  tags,
  side
}: TagColumnProps) {
  return <ul className="pointer-events-none hidden select-none flex-col gap-14 opacity-40 lg:flex" aria-label={side === 'left' ? 'Software' : 'Formate'} data-id="element-296" data-mp="2yxogoe6x56l5">
      {tags.map(tag => <li key={tag.label} className={`flex items-center ${side === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-297" data-mp="19gkjms285nir">
          <span className="shrink-0 rounded-full bg-white px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.08em] text-muted shadow-[0_0_0_1px_rgba(70,28,64,0.08)]" data-id="element-298" data-mp="1eb6y543lrw72">
            {tag.label}
          </span>
          <span className={`h-px flex-1 border-t border-dotted border-tint/40 ${side === 'left' ? 'ml-2 mr-6' : 'ml-6 mr-2'}`} aria-hidden="true" data-id="element-299" data-mp="1s957tw6zb4g8" />
        </li>)}
    </ul>;
}